import { classifyNumberedAnswerKind, parseNumberedSteps } from "./ragChatUtils";

/** Standard OBD-II / J2012 DTC code (P/U/B/C + digit + 3 hex). */
export const DTC_CODE_RE = /\b([PBCU][0-9][0-9A-F]{3})\b/i;

const WARNINGS_SECTION_RE =
  /(?:^|\n)\s*\*{0,2}\s*warnings?\s*(?:and|\/|&)\s*notes?\s*\*{0,2}\s*:?\s*(?:\n|$)/i;

const CANONICAL_SECTION_LABELS: Record<string, string> = {
  description: "Description",
  "possible causes": "Possible Causes",
  "diagnostic aids": "Diagnostic Aids",
  "diagnostic information": "Diagnostic Aids",
  application: "Application",
  "cross references": "Cross References",
  "cross reference": "Cross References",
  "related information": "Related Information",
  symptoms: "Symptoms",
  "diagnostic procedure": "Diagnostic Procedure",
  tests: "Tests",
  "related codes": "Related Codes",
  notes: "Notes",
  warnings: "Warnings",
  references: "Cross References",
  "recommended fix": "Recommended Fix",
};

const DTC_MARKER_SECTIONS = new Set([
  "Description",
  "Possible Causes",
  "Diagnostic Aids",
  "Application",
  "Cross References",
  "Related Information",
]);

const BULLET_LINE_RE = /^[-*•]\s+/;
const MARKDOWN_ONLY_LINE_RE = /^(\*{1,3}|#{1,6}|_+)$/;

export type DtcSectionKind = "prose" | "list";

export interface DtcContentSection {
  label: string;
  kind: DtcSectionKind;
  text?: string;
  items?: string[];
}

export interface ParsedDtcAnswer {
  code: string;
  category: string;
  title: string;
  sections: DtcContentSection[];
}

/** True when a line is only markdown syntax with no meaningful content. */
export function isMarkdownOnlyLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return true;
  return MARKDOWN_ONLY_LINE_RE.test(trimmed);
}

/** Strip heading/title markdown wrappers while preserving citation tokens like [p.2]. */
export function stripMarkdownDecorators(text: string): string {
  let value = String(text ?? "").trim();
  if (!value) return "";

  value = value.replace(/^#{1,6}\s+/, "");
  value = value.replace(/^\*{0,2}Title\s*:\*{0,2}\s*/i, "");
  value = value.replace(/^Title\s*:\*{0,2}\s*/i, "");
  value = value.replace(/^\*{1,3}\s+/, "");
  value = value.replace(/^\*{1,3}(?=[A-Za-z0-9\[(])/, "");
  value = value.replace(/\s*\*{1,3}$/, "");
  return value.trim();
}

function canonicalSectionLabel(raw: string): string | null {
  const key = stripMarkdownDecorators(raw).replace(/:$/, "").trim().toLowerCase();
  return CANONICAL_SECTION_LABELS[key] ?? null;
}

function normalizeHeadingCandidate(line: string): string {
  return stripMarkdownDecorators(line).replace(/:$/, "").trim();
}

function isSectionHeadingLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed || isMarkdownOnlyLine(trimmed)) return false;
  if (/^\*{0,2}Title\s*:/i.test(trimmed)) return true;
  if (/^for\s+.+/i.test(trimmed) && !/^for\s+all\b/i.test(trimmed)) return false;

  const normalized = normalizeHeadingCandidate(trimmed);
  if (canonicalSectionLabel(normalized)) {
    const inlineBody = trimmed.match(
      /^(?:#{1,6}\s+|\*{0,2})[A-Za-z][A-Za-z\s/-]{0,48}(?:\*{0,2})?\s*:\s*(.+)$/
    );
    if (inlineBody?.[1]?.trim()) return true;
    return true;
  }

  return false;
}

function parseSectionHeading(line: string): { label: string; inlineBody?: string } | null {
  const trimmed = line.trim();
  if (!trimmed || isMarkdownOnlyLine(trimmed)) return null;
  if (/^\*{0,2}Title\s*:/i.test(trimmed)) return null;

  const inline = trimmed.match(
    /^(?:#{1,6}\s+|\*{0,2})([A-Za-z][A-Za-z\s/-]{0,48}?)(?:\*{0,2})?\s*:\s*(.+)$/
  );
  if (inline?.[1]) {
    const label = canonicalSectionLabel(inline[1]);
    if (label) {
      const inlineBody = stripMarkdownDecorators(inline[2] ?? "");
      return inlineBody ? { label, inlineBody } : { label };
    }
  }

  const bareLabel = canonicalSectionLabel(normalizeHeadingCandidate(trimmed));
  if (bareLabel) return { label: bareLabel };

  return null;
}

export function dtcCategoryLabel(code: string): string {
  const letter = (code[0] ?? "P").toUpperCase();
  switch (letter) {
    case "B":
      return "Body · B-code";
    case "C":
      return "Chassis · C-code";
    case "U":
      return "Network · U-code";
    default:
      return "Powertrain · P-code";
  }
}

export function extractPrimaryDtcCode(text: string): string | null {
  const body = String(text ?? "");

  const titleLine = body.match(
    /^\*{0,2}Title\s*:\*{0,2}\s*([PBCU][0-9][0-9A-F]{3})\b/im
  );
  if (titleLine?.[1]) return titleLine[1].toUpperCase();

  const boldLead = body.match(/^\s*\*\*([PBCU][0-9][0-9A-F]{3})\*\*/im);
  if (boldLead?.[1]) return boldLead[1].toUpperCase();

  const match = body.match(DTC_CODE_RE);
  return match?.[1]?.toUpperCase() ?? null;
}

function cleanDtcTitle(raw: string, code: string): string {
  let title = stripMarkdownDecorators(raw);
  if (!title) return "";

  title = title.replace(
    new RegExp(`^${code}\\s*[-–—:]\\s*`, "i"),
    ""
  );
  title = title.replace(
    /^[PBCU][0-9][0-9A-F]{3}\s*[-–—:]\s*/i,
    ""
  );
  return stripMarkdownDecorators(title);
}

function parseBulletItem(line: string): string | null {
  const trimmed = line.trim();
  if (!trimmed || isMarkdownOnlyLine(trimmed)) return null;
  const match = trimmed.match(/^[-*•]\s+(.+)$/);
  if (!match?.[1]) return null;
  const item = stripMarkdownDecorators(match[1]);
  return item || null;
}

function cleanContentLine(line: string): string | null {
  const trimmed = line.trim();
  if (!trimmed || isMarkdownOnlyLine(trimmed)) return null;
  if (BULLET_LINE_RE.test(trimmed)) return null;
  const cleaned = stripMarkdownDecorators(trimmed);
  if (!cleaned || isMarkdownOnlyLine(cleaned)) return null;
  return cleaned;
}

function finishSection(section: { label: string; lines: string[] }): DtcContentSection | null {
  const bulletItems: string[] = [];
  const proseLines: string[] = [];

  for (const rawLine of section.lines) {
    const bullet = parseBulletItem(rawLine);
    if (bullet) {
      bulletItems.push(bullet);
      continue;
    }
    const prose = cleanContentLine(rawLine);
    if (prose) proseLines.push(prose);
  }

  if (bulletItems.length > 0 && proseLines.length === 0) {
    return { label: section.label, kind: "list", items: bulletItems };
  }

  const text = proseLines.join("\n\n").trim();
  if (text && bulletItems.length > 0) {
    return {
      label: section.label,
      kind: "prose",
      text: `${text}\n\n${bulletItems.map((item) => `- ${item}`).join("\n")}`,
    };
  }

  if (bulletItems.length > 0) {
    return { label: section.label, kind: "list", items: bulletItems };
  }

  if (!text) return null;

  return {
    label: section.label,
    kind: "prose",
    text,
  };
}

function parseTitleFromLine(line: string, code: string): string | null {
  const trimmed = line.trim();
  if (!trimmed || isMarkdownOnlyLine(trimmed)) return null;

  const titleField = trimmed.match(
    /^\*{0,2}Title\s*:\*{0,2}\s*(?:([PBCU][0-9][0-9A-F]{3})\s*[-–—:]\s*)?(.*)$/i
  );
  if (titleField) {
    const fromField = cleanDtcTitle(titleField[2] ?? titleField[1] ?? "", code);
    return fromField || null;
  }

  const boldWrappedCodeTitle = trimmed.match(
    /^\*\*([PBCU][0-9][0-9A-F]{3})\s*[-–—:]\s*(.+?)\*\*$/i
  );
  if (boldWrappedCodeTitle?.[2]) {
    return cleanDtcTitle(boldWrappedCodeTitle[2], code) || null;
  }

  const boldTitle = trimmed.match(
    /^\*\*([PBCU][0-9][0-9A-F]{3})\*\*\s*[-–—]\s*(.+)$/i
  );
  if (boldTitle?.[2]) {
    return cleanDtcTitle(boldTitle[2], code) || null;
  }

  const plainCodeTitle = trimmed.match(
    /^([PBCU][0-9][0-9A-F]{3})\s*[-–—:]\s*(.+)$/i
  );
  if (plainCodeTitle?.[2] && !isSectionHeadingLine(trimmed)) {
    return cleanDtcTitle(plainCodeTitle[2], code) || null;
  }

  if (!isSectionHeadingLine(trimmed)) {
    const cleaned = cleanDtcTitle(trimmed, code);
    return cleaned || null;
  }

  return null;
}

function hasDtcStructureMarkers(text: string, sections: DtcContentSection[]): boolean {
  if (/^\*{0,2}Title\s*:/im.test(text)) return true;
  if (
    /(?:^|\n)\s*(?:#{1,6}\s+|\*{0,2})(?:Description|Possible Causes|Diagnostic Aids|Application|Cross References)/im.test(
      text
    )
  ) {
    return true;
  }
  return sections.some((s) => DTC_MARKER_SECTIONS.has(s.label));
}

/** Parse a DTC catalog answer into structured header + sections. */
export function parseDtcAnswer(text: string): ParsedDtcAnswer | null {
  let body = String(text ?? "").trim();
  if (!body) return null;

  const warnMatch = body.match(WARNINGS_SECTION_RE);
  if (warnMatch?.index !== undefined) {
    body = body.slice(0, warnMatch.index).trim();
  }

  const code = extractPrimaryDtcCode(body);
  if (!code) return null;

  const lines = body.split("\n");
  let title = "";
  let index = 0;

  while (index < lines.length) {
    const line = lines[index]?.trim() ?? "";
    if (!line || isMarkdownOnlyLine(line)) {
      index++;
      continue;
    }

    const parsedTitle = parseTitleFromLine(line, code);
    if (parsedTitle) {
      title = parsedTitle;
      index++;
      break;
    }

    const boldCodeOnly = line.match(/^\*\*([PBCU][0-9][0-9A-F]{3})\*\*$/i);
    if (boldCodeOnly) {
      const next = lines[index + 1]?.trim() ?? "";
      if (next && !isSectionHeadingLine(next) && !isMarkdownOnlyLine(next)) {
        title = cleanDtcTitle(next, code);
        index += 2;
        break;
      }
      index++;
      break;
    }

    if (isSectionHeadingLine(line)) break;

    title = cleanDtcTitle(line, code);
    index++;
    break;
  }

  const sections: DtcContentSection[] = [];
  let current: { label: string; lines: string[] } | null = null;

  const pushCurrent = () => {
    if (!current) return;
    const finished = finishSection(current);
    if (finished) sections.push(finished);
    current = null;
  };

  for (; index < lines.length; index++) {
    const trimmed = (lines[index] ?? "").trim();
    if (!trimmed) continue;
    if (isMarkdownOnlyLine(trimmed)) continue;

    const heading = parseSectionHeading(trimmed);
    if (heading) {
      pushCurrent();
      current = {
        label: heading.label,
        lines: heading.inlineBody ? [heading.inlineBody] : [],
      };
      continue;
    }

    if (/^For\s+.+/i.test(trimmed)) {
      pushCurrent();
      current = { label: stripMarkdownDecorators(trimmed), lines: [] };
      continue;
    }

    if (!current) {
      const prose = cleanContentLine(trimmed);
      if (prose) current = { label: "Description", lines: [prose] };
    } else {
      current.lines.push(trimmed);
    }
  }
  pushCurrent();

  if (!hasDtcStructureMarkers(body, sections)) return null;
  if (!title && sections.length === 0) return null;

  return {
    code,
    category: dtcCategoryLabel(code),
    title,
    sections,
  };
}

/** True when the answer should use the structured DTC card (not procedure/spec/parts). */
export function isDtcAnswer(text: string): boolean {
  const steps = parseNumberedSteps(text);
  if (steps?.some((s) => s.num)) {
    if (classifyNumberedAnswerKind(steps) === "procedure") return false;
  }
  return parseDtcAnswer(text) !== null;
}
